<ng-container *transloco="let t">
  @switch (componentState()) {
    @case ('loading') {
      <div class="card spinner-card shadow">
        <mop-spinner [text]="t('ams.ams.loadingUi')"></mop-spinner>
      </div>
    }

    @case ('failed') {
      <div class="card error-card shadow mx-2">
        <div class="card-header">
          <span class="h3">
            {{ t('ams.ams.failedToInitializeAmsTitle') }}
          </span>
        </div>

        <div class="card-body">
          <mop-alert [type]="'danger'">
            {{ t('ams.ams.failedToInitializeAms') }}
          </mop-alert>
        </div>

        <div class="card-footer d-flex justify-content-end gap-2">
          <mop-button
            [type]="'secondary'"
            [style]="'outline'"
            [text]="t('ams.ams.logout')"
            (buttonClicked)="logout()"
          ></mop-button>

          <mop-button
            [type]="'primary'"
            [style]="'solid'"
            [text]="t('ams.ams.retry')"
            (buttonClicked)="reInitialize()"
          ></mop-button>
        </div>
      </div>
    }

    @case ('initialized') {
      <mop-app-shell>
        <mop-navbar
          [appName]="t('ams.ams.applicationName')"
          (logoClicked)="handleLogoClick()"
        >
          <mop-theme-toggle
            class="d-none d-lg-block"
            (themeChanged)="handleThemeChanged($event)"
          ></mop-theme-toggle>

          @if (showOrderTracking$ | async) {
            <order-tracking></order-tracking>
          }

          @if (showSwitchAccounts$ | async) {
            <switch-accounts></switch-accounts>
          }

          <lib-support-chat class="d-none d-md-block"></lib-support-chat>

          <mop-button
            [ariaLabel]="t('ams.ams.headerHelp')"
            [size]="'lg'"
            [icon]="'help'"
            [iconOnly]="true"
            tooltip="{{ t('ams.ams.headerHelp') }}"
            placement="bottom"
            (buttonClicked)="openHelpUrl()"
          ></mop-button>

          <tmx-web-tools-dropdown
            [webToolItems]="(webToolItems$ | async)!"
            (click)="handleWebToolsMenuClick()"
            (webToolItemClickEvent)="handleWebToolItemClick($event)"
          ></tmx-web-tools-dropdown>

          <tmx-profile-dropdown
            [ariaLabel]="t('ams.ams.profileDropdownAriaLabel')"
            [firstName]="currentUser().firstName ?? ''"
            [lastName]="currentUser().lastName ?? ''"
            [email]="currentUser().username"
            [profileImageSource]="profileImgSource()"
            [dropdownTooltip]="profileTooltip"
            [appVersionText]="appVersionText()"
            (signOutClicked)="logout()"
          >
            <div class="p-3" ngProjectAs="profile-info">
              <div style="border-top: 1px solid #e0e1e9"></div>

              <div class="d-flex flex-column mt-3 profile-content gap-2">
                @if (companyName(); as company) {
                  <div>
                    <span>{{ t('ams.ams.company') }}&nbsp;</span>
                    <strong>{{ company }}</strong>
                  </div>
                }

                @if (companyId(); as companyId) {
                  <div>
                    <span>{{ t('ams.ams.companyId') }}&nbsp;</span>
                    <strong>{{ companyId }}</strong>
                  </div>
                }

                @if (partnerInfo(); as partnerInfo) {
                  <div>
                    <span>{{ t('ams.ams.partner') }}&nbsp;</span>
                    <strong>{{ partnerInfo }}</strong>
                  </div>
                }

                @if (externalAccountId(); as externalAccountId) {
                  <div>
                    <span>{{ t('ams.ams.externalAccountId') }}&nbsp;</span>
                    <strong>{{ externalAccountId }}</strong>
                  </div>
                }
              </div>
            </div>
          </tmx-profile-dropdown></mop-navbar
        >

        <mop-side-nav>
          <div sideNav></div>
        </mop-side-nav>

        <div class="main-content-wrapper" mainContent>
          @if (isViewingChildAccount$ | async) {
            <div class="message message-primary justify-content-center mb-0">
              <span
                ><i class="modus-icons notranslate me-1" aria-hidden="true"
                  >warning</i
                ></span
              >
              <span
                >{{ t('ams.ams.youAreViewing') }}
                @if (currentUser(); as user) {
                  <span
                    ><strong>{{
                      user.accountName ? user.accountName : ''
                    }}</strong></span
                  >
                }
              </span>
            </div>
          }
          <mop-toast-outlet top="5rem" right="1rem"></mop-toast-outlet>
          <div class="main-content">
            <braze-card-viewer></braze-card-viewer>
            <router-outlet class="d-none"></router-outlet>
          </div>
        </div>

        <mop-footer>
          <div class="small d-flex align-items-center gap-1">
            <span>
              {{ appVersionText() }}
            </span>

            <span>
              <span>|</span>
              {{ currentUser().username }}
            </span>

            <div class="d-none d-md-block">
              <span>|</span>
              {{ userRoles() }}
              <span></span>
            </div>

            <div class="d-none d-md-block">
              <span>|</span>
              {{ companyName() }} ({{ companyId() }})
              <span></span>
            </div>
          </div>
          <div class="small d-flex align-items-center gap-1">
            <div>
              <span>&copy; {{ currentYear }}, Trimble Inc.</span>
            </div>

            <div class="d-none d-md-block">
              <span aria-hidden="true">|</span
              ><a routerLink="terms" rel="license" class="ms-2 me-1">{{
                t('ams.ams.termsOfUse')
              }}</a>
            </div>

            <div class="d-none d-md-block">
              <span aria-hidden="true">|</span
              ><a
                target="_blank"
                href="https://maps.trimble.com/privacy/"
                rel="noopener"
                class="ms-2"
                >{{ t('ams.ams.privacy') }}</a
              >
            </div>
          </div>
        </mop-footer>
      </mop-app-shell>
      <mop-tour-progress
        data-tour-poi="tourProgress"
        [onboardingShifted]="onboardingShifted()"
      ></mop-tour-progress>
    }
  }
</ng-container>

<ng-template #profileTooltip>
  @if (currentUser(); as currentUser) {
    <div class="d-flex flex-column">
      <div>{{ currentUser.firstName }} {{ currentUser.lastName }}</div>
      <div>{{ currentUser.username }}</div>
    </div>
  }
</ng-template>
